import { useEffect, useMemo } from 'react'

import { FetchStatus } from '@/constants/general'
import { ContentStackStore } from '@/services/content-stack/content-stack.store'
import { useFetchEntry } from '@/services/content-stack/hooks/useFetchEntry'
import {
  UseCMSAsyncFetchOptions,
  UseCMSAsyncFetchReturn,
  UseCMSEnsureEntryFulfilledOptions,
  UseCMSReturn,
  UseCMSShareEntry,
  UseCMSSyncFetchOptions,
} from '@/services/content-stack/types'

/**
 * Sync entry fetch
 * Provide the parameters necessary to get the entry when calling the hook
 */
export function useContentStack<RawEntry, Entry = RawEntry>(
  options: UseCMSSyncFetchOptions<RawEntry, Entry>,
): UseCMSReturn<Entry>

/**
 * Shared entry.
 * Provide the same entry dispatched for another component and follow the same entry response
 */
export function useContentStack<Entry>(
  options: UseCMSShareEntry,
): UseCMSReturn<Entry>

/**
 * Share entry results and ensures entry is already gotten.
 * Provide the same entry dispatched and fulfilled by another component.
 * Makes sure the component that is using this option will only be rendered when the entry is fulfilled
 * otherwise it can throw an error in the application
 */
export function useContentStack<Entry>(
  options: UseCMSEnsureEntryFulfilledOptions,
): Required<UseCMSReturn<Entry>>

/**
 * Async entry fetch
 * Provide the parameters for search ahead in a React hook
 */
export function useContentStack<RawEntry, Entry = RawEntry>(
  options: UseCMSAsyncFetchOptions<RawEntry, Entry>,
): UseCMSAsyncFetchReturn<Entry>

export function useContentStack<RawEntry, Entry = RawEntry>(
  options: UseCMSShareEntry &
    Omit<
      Partial<
        UseCMSAsyncFetchOptions<RawEntry, Entry> &
          UseCMSSyncFetchOptions<RawEntry, Entry> &
          UseCMSEnsureEntryFulfilledOptions
      >,
      'entry'
    >,
): UseCMSAsyncFetchReturn<Entry> | UseCMSReturn<Entry> {
  const entryState = ContentStackStore(({ entries }) =>
    entries.get(options.entryParams),
  )

  if (options.ensuresEntryFulfilled && !entryState?.entry) {
    throw new Error(
      'You are using the useContentStack hook without dispatching the entry. Make sure you are dispatching the entry in the main component and preventing the children components to get rendered',
    )
  }

  const fetchCMSEntry = useFetchEntry<RawEntry, Entry>({
    entryParams: options.entryParams,
    parser: options.parser
      ? options.parser
      : (entry: RawEntry) => entry as unknown as Entry,
    onError: options.onError,
  })

  useEffect(() => {
    const isEntryStateUndefinedOrIDLE =
      !entryState || entryState.status === FetchStatus.IDLE
    if (!options.asyncFetch && isEntryStateUndefinedOrIDLE) {
      fetchCMSEntry({ query: options.query || [] })
    }
  }, [entryState, fetchCMSEntry, options])

  return useMemo(
    () => ({
      fetchCMSEntry,
      entry: entryState?.entry as Entry | undefined,
    }),
    [entryState, fetchCMSEntry],
  )
}
